import { Component, ElementRef, EventEmitter, HostListener, Input, Output, Renderer2 } from '@angular/core';

@Component({
  selector: 'arivo-bottom-sheet-dialog',
  templateUrl: './arivo-bottom-sheet-dialog.component.html',
  styleUrl: './arivo-bottom-sheet-dialog.component.scss',
})
export class ArivoBottomSheetDialogComponent {
  @Input() dynamicWidth: boolean = false;
  @Input() gestureClose: boolean = true; // New input to enable/disable swipe gestures
  @Output() onClose: EventEmitter<void> = new EventEmitter<void>();
  @Output() onOpen: EventEmitter<void> = new EventEmitter<void>();

  private _open: boolean = false;
  private startY: number = 0;
  private currentY: number = 0;
  private swipeThreshold: number = 100; // Minimum swipe distance to trigger close
  private isSwiping: boolean = false;
  private contentElement: HTMLElement | null = null;

  get open(): boolean {
    return this._open;
  }

  @Input() set open(value: boolean) {
    this._open = value;
    if (!value) {
      this.onClose.emit();
    } else {
      this.onOpen.emit();
    }
  }

  constructor(
    private renderer: Renderer2,
    private el: ElementRef,
  ) {}

  private getContentElement(): HTMLElement | null {
    if (!this.contentElement) {
      this.contentElement = this.el.nativeElement.querySelector('.bottom-sheet-dialog-content');
    }
    return this.contentElement;
  }

  backdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  close(): void {
    this.open = false;
    this.resetTransform();
  }

  private resetTransform(): void {
    const content = this.getContentElement();
    if (content) {
      this.renderer.setStyle(content, 'transform', 'translate(-50%, 0%)');
    }
  }

  // Listen for touchstart to capture the initial Y position
  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (!this.gestureClose) return; // Check if gestureClose is enabled
    this.startY = event.touches[0].clientY;
    this.currentY = this.startY;
    this.isSwiping = true;
  }

  // Listen for touchmove to update the transform dynamically
  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    const content = this.getContentElement();
    if (!this.gestureClose || !this.isSwiping || !content) return;

    this.currentY = event.touches[0].clientY;
    const deltaY = this.currentY - this.startY;
    const scrollTop = content.scrollTop;

    // Only prevent default if at the top and swiping down
    if (scrollTop === 0 && deltaY > 0) {
      event.preventDefault();
      this.renderer.setStyle(content, 'transform', `translate(-50%, ${deltaY}px)`);
    }
  }

  // Listen for touchend to determine if a swipe down occurred
  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    const content = this.getContentElement();

    if (!this.gestureClose || !this.isSwiping || !content) return;

    const deltaY = this.currentY - this.startY;
    this.isSwiping = false;
    const scrollTop = content.scrollTop;

    // Only close if at the top and swiping down past threshold
    if (scrollTop === 0 && deltaY > this.swipeThreshold) {
      this.close();
    } else {
      this.resetTransform();
    }
  }
}
